import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { financeAPI, clientsAPI, operationsAPI, stationsAPI, invoicesAPI } from '../lib/api';
import { fetchAllPages } from '../lib/fetchAllPages';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { SearchableSelect } from '../components/ui/searchable-select';
import { toast } from '../components/ui/sonner';
import { formatCurrency, formatCompactCurrency, cn } from '../lib/utils';
import { PageHeader } from '../components/ui/page-header';
import { LEXIQUE } from '../lib/lexique';
import { useAuth } from '../context/AuthContext';
import useHasOwnStations from '../hooks/useHasOwnStations';
import { nomLisible, referenceLisible, texteSur } from '../lib/libelles';
import { formatPourcent } from '../lib/formats';
import { messageErreur } from '../lib/erreurs';
import { useRafraichirSur } from '../hooks/useRafraichirSur';
import { positionArgent, reprisesAFaire } from '../lib/argentApi';
import {
  CLE_TIERS,
  ONGLETS_ARGENT,
  ONGLET_PAR_DEFAUT,
  PERIODE_PAR_DEFAUT,
  bornesPeriode,
  ongletDepuis,
} from '../components/finance/argentOnglets';
import SelecteurPeriode from '../components/finance/SelecteurPeriode';
import AFaire, { ageFacture, lignesAFaire } from '../components/finance/AFaire';
import NouvelleOperation, { EncaisserClient } from '../components/finance/NouvelleOperation';
import CaisseBanque from '../components/finance/CaisseBanque';
import ClientsArgent from '../components/finance/ClientsArgent';
import ChauffeursArgent from '../components/finance/ChauffeursArgent';
import FournisseursArgent from '../components/finance/FournisseursArgent';
import Benefice from '../components/finance/Benefice';
import { useEstMobile } from '../hooks/useEstMobile';

/** Marge d'un convoi, ou `null` quand le serveur ne la donne pas encore (convoi en cours). */
export const margeConvoi = (convoi) => {
  const marge = convoi?.margin;
  if (marge === null || marge === undefined || marge === '') return null;
  const n = Number(marge);
  return Number.isFinite(n) ? n : null;
};

const FinancePage = () => {
  const navigate = useNavigate();
  const estMobile = useEstMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabDemande = searchParams.get('tab');
  const activeTab = ongletDepuis(tabDemande);
  // Période dans l'adresse (`?periode=mois-dernier`, `?periode=libre&du=…&au=…`).
  const periode = bornesPeriode(searchParams.get('periode') || PERIODE_PAR_DEFAUT, {
    du: searchParams.get('du'),
    au: searchParams.get('au'),
  });
  const fromDate = periode.du;
  const toDate = periode.au;
  const { hasModule, canSeeMoney, user, canWriteModule } = useAuth();
  // Le personnel n'enregistre de l'argent que si son profil le permet; les
  // autres rôles admis sur cet écran suivent la règle du serveur.
  const peutSaisir = user?.role !== 'PERSONNEL' || Boolean(canWriteModule?.('finance_cockpit'));
  // Module ouvert ET stations réellement exploitées (pack Transport: module par défaut).
  const aDesStations = useHasOwnStations();
  const canAccessStations = hasModule('stations') && aDesStations;
  const [summary, setSummary] = useState(null);
  const [openSummary, setOpenSummary] = useState(null);
  const [cashPosition, setCashPosition] = useState(null);
  const [cashStatus, setCashStatus] = useState('loading');
  const [reprises, setReprises] = useState(null);
  const [openLoading, setOpenLoading] = useState(true);
  const [summaryError, setSummaryError] = useState(false);
  const [openError, setOpenError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [clientId, setClientId] = useState('');
  const [convoiId, setConvoiId] = useState('');
  const [stationId, setStationId] = useState('');

  const [clients, setClients] = useState([]);
  const [encaissement, setEncaissement] = useState(null);
  const [convois, setConvois] = useState([]);
  const [stations, setStations] = useState([]);

  const clientOptions = useMemo(
    () => [
      { value: 'ALL', label: 'Tous' },
      ...clients.map((c) => ({
        value: c.id,
        label: nomLisible(c.name),
        description: c.city || undefined,
        keywords: [c.contact_name, c.phone, c.tax_id],
      })),
    ],
    [clients],
  );
  // Le résumé financier renvoie parfois les factures sans le nom du client
  // (« Sans nom » sur trois lignes pour trois clients différents): le nom
  // se relit dans la liste des clients déjà chargée pour le filtre.
  const nomsClients = useMemo(
    () => new Map(clients.map((c) => [c.id, c.name])),
    [clients],
  );
  const nomClient = (clientId, nom) => nomLisible(nom || nomsClients.get(clientId));
  const convoiOptions = useMemo(
    () => [
      { value: 'ALL', label: 'Tous' },
      ...convois.map((o) => ({
        value: o.id,
        label: referenceLisible(o.code, 'Convoi sans référence'),
        description: [o.beneficiary_name, o.route].filter(Boolean).join(' · ') || undefined,
        keywords: [o.country, o.departure_date],
      })),
    ],
    [convois],
  );
  const stationOptions = useMemo(
    () => [
      { value: 'ALL', label: 'Toutes' },
      ...stations.map((s) => ({
        value: s.id,
        label: nomLisible(s.name),
        description: s.city || undefined,
        keywords: [s.code, s.manager_name],
      })),
    ],
    [stations],
  );

  const loadFilters = useCallback(async () => {
    if (activeTab !== 'benefice' && activeTab !== 'a-faire') return;
    try {
      const [c, o, s] = await Promise.all([
        fetchAllPages((page) => clientsAPI.getAllPaged(page)),
        activeTab === 'benefice'
          ? fetchAllPages((page) => operationsAPI.getAllPaged(page))
          : Promise.resolve({ items: [] }),
        activeTab === 'benefice' && canAccessStations
          ? fetchAllPages((page) => stationsAPI.getAllPaged(page))
          : Promise.resolve({ items: [] }),
      ]);
      setClients(c?.items || []);
      setConvois(o?.items || []);
      setStations(s?.items || []);
    } catch (err) {
      console.warn('Filter options unavailable', err);
    }
  }, [activeTab, canAccessStations]);

  const loadSummary = useCallback(async ({ silencieux = false } = {}) => {
    if (!silencieux) setIsLoading(true);
    try {
      const params = {};
      if (fromDate) params.fromDate = fromDate;
      if (toDate) params.toDate = toDate;
      if (activeTab === 'benefice' && clientId) params.client_id = clientId;
      if (activeTab === 'benefice' && convoiId) params.convoi_id = convoiId;
      if (activeTab === 'benefice' && stationId) params.station_id = stationId;
      const data = await financeAPI.getSummary(params);
      setSummary(data);
      setSummaryError(false);
    } catch (err) {
      setSummaryError(true);
      console.error('Failed to load finance summary', err);
      toast.error(messageErreur(err, 'Impossible de charger les chiffres de l’argent'));
    } finally {
      setIsLoading(false);
    }
  }, [fromDate, toDate, clientId, convoiId, stationId, activeTab]);

  const loadOpenSummary = useCallback(async () => {
    try {
      setOpenSummary(await financeAPI.getSummary());
      setOpenError(false);
    } catch (err) {
      setOpenError(true);
      toast.error(messageErreur(err, 'Impossible de charger les factures à traiter'));
    } finally {
      setOpenLoading(false);
    }
  }, []);

  // Lecture directe des soldes (`/finance/position`), au lieu du bilan
  // mensuel complet des rapports qui mettait 13 à 18 secondes.
  const loadCashPosition = useCallback(async () => {
    if (!canSeeMoney) return;
    try {
      setCashPosition(await positionArgent());
      setCashStatus('ready');
    } catch (err) {
      setCashPosition(null);
      setCashStatus('error');
    }
  }, [canSeeMoney]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadOpenSummary();
  }, [loadOpenSummary]);

  // Les pastilles de l'en-tête sont sur tous les onglets: la position se lit
  // dès l'ouverture (lecture directe, quelques dixièmes de seconde).
  useEffect(() => {
    loadCashPosition();
  }, [loadCashPosition]);

  // Reprises historiques (soldes clients, achats des stations): lignes d'« À faire ».
  const loadReprises = useCallback(async () => {
    try {
      setReprises(await reprisesAFaire());
    } catch (err) {
      setReprises(null);
    }
  }, []);
  useEffect(() => {
    loadReprises();
  }, [loadReprises]);

  // Anciennes adresses (`?tab=apercu`, `a-traiter`, `analyses`, `comptes`):
  // remplacées sans ajouter d'étape à l'historique.
  useEffect(() => {
    if (!tabDemande || tabDemande === activeTab) return;
    setSearchParams((precedent) => {
      const suivant = new URLSearchParams(precedent);
      if (activeTab === ONGLET_PAR_DEFAUT) suivant.delete('tab');
      else suivant.set('tab', activeTab);
      return suivant;
    }, { replace: true });
  }, [tabDemande, activeTab, setSearchParams]);

  // Un encaissement, une dépense ou un trajet clôturé ailleurs change ces
  // totaux: ils se rechargent sans attendre un rechargement manuel.
  const refreshFinance = useCallback(() => {
    loadSummary({ silencieux: true });
    loadOpenSummary();
    loadCashPosition();
    loadReprises();
  }, [loadSummary, loadOpenSummary, loadCashPosition, loadReprises]);
  useRafraichirSur(['finance', 'paiements', 'factures', 'depenses'], refreshFinance);

  const effacerFiltres = () => {
    setClientId('');
    setConvoiId('');
    setStationId('');
  };
  const filtresActifs = Boolean(clientId || convoiId || stationId);

  /** Change d'onglet; `extra` pose ou retire d'autres paramètres (tiers choisi…). */
  const selectTab = (tab, extra = {}) => {
    const nextParams = new URLSearchParams(searchParams);
    if (tab === ONGLET_PAR_DEFAUT) nextParams.delete('tab');
    else nextParams.set('tab', tab);
    Object.entries(extra).forEach(([cle, valeur]) => {
      if (valeur) nextParams.set(cle, valeur);
      else nextParams.delete(cle);
    });
    setSearchParams(nextParams);
  };

  const changerPeriode = ({ cle, du, au }) => {
    setSearchParams((precedent) => {
      const suivant = new URLSearchParams(precedent);
      if (!cle || cle === PERIODE_PAR_DEFAUT) suivant.delete('periode');
      else suivant.set('periode', cle);
      if (cle === 'libre') {
        suivant.set('du', du || periode.du);
        suivant.set('au', au || periode.au);
      } else {
        suivant.delete('du');
        suivant.delete('au');
      }
      return suivant;
    });
  };

  /** Le tiers choisi dans un onglet (client, chauffeur, fournisseur, compte), gardé dans l'adresse. */
  const tiersDe = (onglet) => searchParams.get(CLE_TIERS[onglet]) || '';
  const choisirTiers = (onglet) => (valeur) => {
    setSearchParams((precedent) => {
      const suivant = new URLSearchParams(precedent);
      if (valeur) suivant.set(CLE_TIERS[onglet], valeur);
      else suivant.delete(CLE_TIERS[onglet]);
      return suivant;
    });
  };

  const markBillingReady = async (tripId) => {
    if (!tripId) return;
    try {
      await proofAction(tripId);
      toast.success(`Mission passée en « ${LEXIQUE.pretAFacturer.toLowerCase()} »`);
      await loadOpenSummary();
    } catch (err) {
      console.error('Failed to mark billing ready', err);
      toast.error(messageErreur(err, `Impossible de passer la mission en « ${LEXIQUE.pretAFacturer.toLowerCase()} »`));
    }
  };

  const proofAction = async (tripId) => {
    return financeAPI.markBillingReady(
      tripId,
      `Validation finance depuis la file des ${LEXIQUE.preuvesLivraison.toLowerCase()}`,
    );
  };

  const generateInvoice = async (item) => {
    if (!item?.order_id) {
      toast.error('Commande introuvable pour cette mission');
      return;
    }
    try {
      const invoice = await invoicesAPI.generateForOrder(item.order_id);
      toast.success('Facture générée');
      if (invoice.advisories?.length) toast.info(invoice.advisories.map((avis) => texteSur(avis.message, '')).filter(Boolean).join(' '));
      await loadOpenSummary();
      if (invoice?.id) {
        navigate(`/invoices?invoice_id=${encodeURIComponent(invoice.id)}`);
        return;
      }
      navigate('/invoices');
    } catch (err) {
      console.error('Failed to generate invoice', err);
      toast.error(messageErreur(err, 'Impossible de générer la facture depuis cette mission'));
    }
  };

  const revenue = summary?.revenue || {};
  const margin = summary?.margin || {};
  const receivables = openSummary?.receivables || {};
  const encoursClients = Number(receivables.open_value || 0);
  // L'essentiel en quatre chiffres, en haut de page, chacun avec un mot
  // d'état; le détail suit, rangé par questions. Les pastilles de l'en-tête
  // répétaient ces chiffres en plus petit: l'en-tête ne garde que les alertes.
  const resultatNet = Number(margin.net || 0);
  const unknownCashAccounts = Number(cashPosition?.treasury?.unknown_balance_accounts || 0);
  const comptesTresorerie = cashPosition?.treasury?.accounts || [];
  const chezChauffeurs = cashPosition?.drivers || null;
  const EN_CHARGEMENT = 'Chargement…';
  const valeurDisponible = cashStatus === 'loading'
    ? EN_CHARGEMENT
    : cashStatus === 'error' || !cashPosition
      ? 'Indisponible'
      : !comptesTresorerie.length
        ? 'Aucun compte'
        : unknownCashAccounts === comptesTresorerie.length
          ? 'À compléter'
          : formatCurrency(cashPosition.treasury.total || 0);
  // Les quatre chiffres clés, en pastilles cliquables sur tous les onglets:
  // chacune ouvre l'onglet qui la détaille. Seul le bénéfice suit la période.
  const pourcentBenefice = Number(revenue.total || 0) > 0 ? formatPourcent(Number(margin.net_pct || 0), 1) : null;
  const statsArgent = [
    canSeeMoney && {
      label: 'Disponible',
      valeur: valeurDisponible,
      ton: unknownCashAccounts > 0 ? 'attention' : undefined,
      aide: unknownCashAccounts > 0
        ? `${unknownCashAccounts} compte${unknownCashAccounts > 1 ? 's' : ''} à compléter`
        : 'Caisses et banques, à aujourd’hui',
      onClick: () => selectTab('caisse'),
      testid: 'argent-stat-disponible',
    },
    {
      label: 'Clients doivent',
      valeur: !openSummary && openLoading ? EN_CHARGEMENT : openError && !openSummary ? 'Indisponible' : formatCurrency(encoursClients),
      aide: 'Factures à encaisser, toutes périodes',
      onClick: () => selectTab('clients'),
      testid: 'argent-stat-clients',
    },
    // Personne n'a d'argent à justifier: la pastille ne s'affiche pas.
    canSeeMoney && Number(chezChauffeurs?.holding) > 0 && {
      label: 'Chez chauffeurs',
      valeur: formatCurrency(chezChauffeurs.holding || 0),
      aide: 'Avances pas encore justifiées ni rendues',
      onClick: () => selectTab('chauffeurs'),
      testid: 'argent-stat-chauffeurs',
    },
    {
      label: `${summary && resultatNet < 0 ? LEXIQUE.perte : LEXIQUE.benefice} ${periode.court}`,
      valeur: !summary && isLoading ? EN_CHARGEMENT : summaryError && !summary ? 'Indisponible' : formatCurrency(Math.abs(resultatNet)),
      ton: !summary ? undefined : resultatNet > 0 ? 'positif' : resultatNet < 0 ? 'critique' : undefined,
      aide: [`Recettes moins dépenses · ${periode.libelle}`, pourcentBenefice ? `${pourcentBenefice} des recettes` : null].filter(Boolean).join(' · '),
      onClick: () => selectTab('benefice'),
      testid: 'argent-stat-benefice',
    },
  ].filter(Boolean);
  const alertesArgent = [
    (summary?.totals_incomplete || openSummary?.totals_incomplete) && {
      niveau: 'attention',
      court: 'Montants à rapprocher',
      texte:
        'Montants historiques à rapprocher : ces totaux ne comprennent que les montants exploitables. Consultez les pièces et le rapport historique dans les relevés.',
    },
  ].filter(Boolean);

  const openInvoiceAction = (item, fallbackAction = 'payment') => {
    if (!item?.invoice_id) {
      navigate('/invoices');
      return;
    }
    const action = item.action || fallbackAction;
    navigate(
      `/invoices?invoice_id=${encodeURIComponent(item.invoice_id)}&invoice_action=${encodeURIComponent(action)}`,
    );
  };

  // « À faire »: une ligne par dossier, groupe par groupe (facturer, encaisser,
  // confirmer, contrôler, justifier, reprendre); son total fait le compteur.
  const donneesAFaire = lignesAFaire({ openSummary, position: cashPosition, reprises, nomClient });
  const agirAFaire = (ligne) => {
    const { action } = ligne;
    if (action.type === 'facturer') generateInvoice(action.item);
    else if (action.type === 'pret') markBillingReady(action.item?.trip_id);
    else if (action.type === 'encaisser') {
      // La même fenêtre « Encaisser un client » que partout, sur place.
      if (action.item?.client_id && peutSaisir) setEncaissement({ client: action.item.client_id, facture: action.item.invoice_id });
      else openInvoiceAction(action.item, 'payment');
    }
    else if (action.type === 'route') navigate(action.route);
    else if (action.type === 'onglet') selectTab(action.onglet, action.extra);
  };

  // Compteurs des onglets: on sait où aller sans ouvrir.
  const nbAFaire = donneesAFaire.total;
  const compteurs = {
    'a-faire': openSummary ? nbAFaire : null,
    caisse: unknownCashAccounts > 0 ? 'point' : null,
    clients: openSummary ? Number(receivables.open_count || 0) : null,
    chauffeurs: chezChauffeurs ? Number(chezChauffeurs.holding_count || 0) : null,
    fournisseurs: cashPosition?.suppliers ? Number(cashPosition.suppliers.owed_count || 0) : null,
  };

  return (
    <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
      <PageHeader
        eyebrow="Encaisser et payer"
        title="Argent"
        description="Disponible, clients et chauffeurs : à aujourd’hui. Bénéfice : selon la période choisie dans l’onglet Bénéfice."
        stats={estMobile ? undefined : statsArgent}
        alertesSousTitre
        alertes={alertesArgent}
        actions={peutSaisir ? (
          <NouvelleOperation
            data-header-primary
            // Depuis un onglet où un tiers est choisi, la fenêtre le propose déjà.
            prefill={{
              client: tiersDe('clients'),
              chauffeur: tiersDe('chauffeurs'),
              fournisseur: tiersDe('fournisseurs'),
              compte: tiersDe('caisse'),
            }}
            onEnregistre={refreshFinance}
          />
        ) : undefined}
      />
      {estMobile && <PastillesMobile stats={statsArgent} />}

      <Tabs value={activeTab} onValueChange={(onglet) => selectTab(onglet)} className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TabsList className="flex h-auto max-w-full justify-start gap-1 overflow-x-auto" aria-label="Onglets de l’écran Argent">
            {ONGLETS_ARGENT.map((tab) => (
              <TabsTrigger key={tab.value} value={tab.value} className="shrink-0 gap-1.5 px-3 py-1.5">
                <span className="sm:hidden">{tab.court}</span>
                <span className="hidden sm:inline">{tab.label}</span>
                <CompteurOnglet valeur={compteurs[tab.value]} attention={tab.value === 'a-faire'} />
              </TabsTrigger>
            ))}
          </TabsList>
          {(activeTab === 'benefice' || activeTab === 'caisse') && <SelecteurPeriode periode={periode} onChange={changerPeriode} />}
        </div>

      <TabsContent value="a-faire">
        <AFaire
          donnees={donneesAFaire}
          chargement={openLoading && !openSummary}
          erreur={openError && !openSummary ? <Indisponible texte="Les actions sont indisponibles." onRetry={refreshFinance} /> : null}
          position={cashPosition}
          positionStatut={cashStatus}
          voitArgent={canSeeMoney}
          onAction={agirAFaire}
          onOnglet={(onglet, extra) => selectTab(onglet, extra)}
        />
      </TabsContent>

      <TabsContent value="caisse" className="space-y-5">
        {canSeeMoney ? (
          <CaisseBanque
            position={cashPosition}
            positionStatut={cashStatus}
            compteChoisi={tiersDe('caisse')}
            onChoisirCompte={choisirTiers('caisse')}
            periode={periode}
            onToutesDates={() => changerPeriode({ cle: 'tout' })}
            peutSaisir={peutSaisir}
            onModifie={refreshFinance}
          />
        ) : (
          <p className="rounded-xl border bg-card px-3 py-2 text-sm text-muted-foreground">Les soldes des comptes sont réservés à la direction.</p>
        )}
      </TabsContent>

      <TabsContent value="clients" className="space-y-5">
        {canSeeMoney ? (
          <ClientsArgent
            clientChoisi={tiersDe('clients')}
            onChoisirClient={choisirTiers('clients')}
            peutSaisir={peutSaisir}
            soldesARapprocher={Number(reprises?.soldesClients || 0)}
            onModifie={refreshFinance}
          />
        ) : (
          <p className="rounded-xl border bg-card px-3 py-2 text-sm text-muted-foreground">Les soldes des clients sont réservés à la direction.</p>
        )}
      </TabsContent>

      <TabsContent value="chauffeurs" className="space-y-5">
        {canSeeMoney ? (
          <ChauffeursArgent
            chauffeurChoisi={tiersDe('chauffeurs')}
            onChoisirChauffeur={choisirTiers('chauffeurs')}
            peutSaisir={peutSaisir}
            // Inconnu tant que la position se lit: le filtre par défaut en dépend.
            nombreAJustifier={cashStatus === 'loading' ? null : Number(chezChauffeurs?.holding_count || 0)}
            onModifie={refreshFinance}
          />
        ) : (
          <p className="rounded-xl border bg-card px-3 py-2 text-sm text-muted-foreground">L’argent des chauffeurs est réservé à la direction.</p>
        )}
      </TabsContent>

      <TabsContent value="fournisseurs" className="space-y-5">
        {canSeeMoney ? (
          <FournisseursArgent
            fournisseurChoisi={tiersDe('fournisseurs')}
            onChoisirFournisseur={choisirTiers('fournisseurs')}
            peutSaisir={peutSaisir}
            nombreAPayer={cashStatus === 'loading' ? null : Number(cashPosition?.suppliers?.owed_count || 0)}
            achatsAReprendre={Number(reprises?.achats || 0)}
            avecLots={hasModule('lots')}
            avecLocations={hasModule('truck_rentals')}
            onModifie={refreshFinance}
          />
        ) : (
          <p className="rounded-xl border bg-card px-3 py-2 text-sm text-muted-foreground">Les dettes fournisseurs sont réservées à la direction.</p>
        )}
      </TabsContent>

      <TabsContent value="benefice" className="space-y-5">
        <Benefice
          summary={summary}
          chargement={isLoading}
          erreur={summaryError && !summary ? <Indisponible texte="Le résultat de la période est indisponible." onRetry={refreshFinance} /> : null}
          convois={convois}
          nomClient={nomClient}
          periodeLibelle={periode.libelle}
          lienRapport={hasModule('reports') ? '/rapports?onglet=bilan' : null}
          filtres={(
            <FiltresBenefice estMobile={estMobile} actifs={[clientId, convoiId, stationId].filter(Boolean).length}>
            <div className="flex flex-wrap items-center gap-2" data-testid="filtres-benefice">
              <label className="flex items-center gap-1.5 text-sm">
                <span className="text-muted-foreground">Client</span>
                <SearchableSelect
                  className="h-8 w-48 sm:w-56"
                  value={clientId || 'ALL'}
                  onValueChange={(v) => setClientId(v === 'ALL' ? '' : v)}
                  options={clientOptions}
                  placeholder="Tous"
                  searchPlaceholder="Rechercher un client…"
                  emptyMessage="Aucun client trouvé"
                />
              </label>
              <label className="flex items-center gap-1.5 text-sm">
                <span className="text-muted-foreground">Convoi</span>
                <SearchableSelect
                  className="h-8 w-48 sm:w-56"
                  value={convoiId || 'ALL'}
                  onValueChange={(v) => setConvoiId(v === 'ALL' ? '' : v)}
                  options={convoiOptions}
                  placeholder="Tous"
                  searchPlaceholder="Rechercher un convoi…"
                  emptyMessage="Aucun convoi trouvé"
                />
              </label>
              {canAccessStations && <label className="flex items-center gap-1.5 text-sm">
                <span className="text-muted-foreground">Station</span>
                <SearchableSelect
                  className="h-8 w-48 sm:w-56"
                  value={stationId || 'ALL'}
                  onValueChange={(v) => setStationId(v === 'ALL' ? '' : v)}
                  options={stationOptions}
                  placeholder="Toutes"
                  searchPlaceholder="Rechercher une station…"
                  emptyMessage="Aucune station trouvée"
                />
              </label>}
              {filtresActifs && <Button variant="ghost" size="sm" className="h-8" onClick={effacerFiltres}>Effacer</Button>}
            </div>
            </FiltresBenefice>
          )}
        />
      </TabsContent>
      </Tabs>
      <EncaisserClient
        ouvert={Boolean(encaissement)}
        client={encaissement?.client}
        facture={encaissement?.facture}
        onFermer={() => setEncaissement(null)}
        onTermine={() => {
          setEncaissement(null);
          refreshFinance();
        }}
      />
    </div>
  );
};

/** Sur téléphone, les filtres du bénéfice se replient derrière « Filtres (n) ». */
const FiltresBenefice = ({ estMobile, actifs, children }) => (estMobile ? (
  <details className="depliant rounded-lg border bg-card px-3 py-2">
    <summary className="cursor-pointer text-sm font-medium">{`Filtres${actifs ? ` (${actifs})` : ''}`}</summary>
    <div className="pt-2">{children}</div>
  </details>
) : children);

/** Un chiffre indisponible: le dire, et proposer de réessayer sur place. */
const Indisponible = ({ texte, onRetry }) => (
  <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-destructive/30 p-3 text-sm text-destructive">
    <span>{texte}</span>
    <Button variant="outline" size="sm" className="h-7" onClick={onRetry}>Réessayer</Button>
  </div>
);

/** Compteur d'un onglet: nombre (orange pour « À faire ») ou point quand un solde manque. */
const CompteurOnglet = ({ valeur, attention = false }) => {
  if (valeur === 'point') {
    return <span className="h-2 w-2 rounded-full bg-flame" aria-label="solde à compléter" role="img" />;
  }
  if (!Number(valeur)) return null;
  return (
    <span
      className={cn(
        'min-w-[1.25rem] rounded-full px-1.5 text-center text-[11px] font-semibold tabular-nums leading-5',
        attention ? 'bg-flame/15 text-flame-ink' : 'bg-muted-foreground/10 text-muted-foreground',
      )}
    >
      {Number(valeur).toLocaleString('fr-FR')}
    </span>
  );
};

/** Sur téléphone, les pastilles restent visibles en une rangée qui défile. */
const PastillesMobile = ({ stats }) => (
  <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1" role="group" aria-label="Chiffres clés">
    {stats.map((stat) => {
      const compacte = stat.valeurCompacte || (/FCFA$/.test(String(stat.valeur)) ? formatCompactCurrency(Number(String(stat.valeur).replace(/[^\d-]/g, ''))) : stat.valeur);
      return (
        <button
          key={stat.label}
          type="button"
          onClick={stat.onClick}
          title={`${stat.label} — ${stat.valeur}`}
          className="flex shrink-0 flex-col items-start rounded-lg border bg-card px-3 py-1.5 text-left"
        >
          <span className="text-[11px] text-muted-foreground">{stat.label}</span>
          <span className={cn('text-sm font-semibold tabular-nums', stat.ton === 'attention' && 'text-flame-ink', stat.ton === 'critique' && 'text-destructive', stat.ton === 'positif' && 'text-success')}>{compacte}</span>
        </button>
      );
    })}
  </div>
);

/** Le compte d'une file: « 10 sur 12 » plutôt qu'un « 10 » qui cache deux factures. */
export const compteFile = (affichees, total) => {
  const n = Number(total);
  return Number.isFinite(n) && n > affichees ? `${affichees} sur ${n}` : String(affichees);
};

export { ageFacture };

export default FinancePage;
